import { atom, useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import * as React from 'react';

/**
 * Identity of the session-list link popover that is currently open, or
 * `null` when none is. Module-level so the coordinated link affordances
 * share one source of truth: the session-row avatars in both the
 * consolidated and data-table lists, and the side-nav bulk-link footer —
 * everything that renders `SessionListLinkPopover` or the legacy
 * `LinkSessionsComboBoxResponsive`. (The data-table bulk overlay renders
 * `SessionListLinkPopoverContent` directly with caller-owned open state,
 * so it is intentionally outside this coordination.)
 */
const openLinkPopoverKeyAtom = atom<string | null>(null);

/**
 * Controlled open state for a session-list link popover that guarantees at
 * most one is open at a time across the coordinated affordances.
 *
 * Each affordance previously owned isolated `useState(false)` open state,
 * so opening one popover did nothing to the others — nothing coordinated
 * them, and dismissal was left entirely to Radix's click-away, which the
 * row's `stopPropagation` wrapper suppresses (the wrapper stops the click
 * before it can reach the document-level listener Radix dismisses on). The
 * result was several "Search for a patient" popovers stacked open at once.
 *
 * Identity is a per-instance `useId()`, NOT anything derived from the
 * session ids: two affordances with the same selection (e.g. a row avatar
 * and a single-selection bulk footer) must not collapse to one key, or one
 * atom write would open both — reproducing the stacking bug. A unique id
 * per instance keeps "open one → close the rest" while making collisions
 * impossible.
 *
 * Lives in the patients package beside the session-list link popover, so the
 * coordinated affordances share one jotai atom.
 */
export function useSingleOpenLinkPopover(): readonly [boolean, (next: boolean) => void] {
  const id = React.useId();

  // Subscribe only to whether THIS instance is the open one, so an
  // open/close re-renders just the two affordances whose boolean flips —
  // not every mounted row in a long or virtualised list.
  const isOpenAtom = React.useMemo(
    () => selectAtom(openLinkPopoverKeyAtom, (key) => key === id),
    [id]
  );
  const open = useAtomValue(isOpenAtom);
  // `useSetAtom` does not subscribe, so writes never re-render the writer.
  const setOpenKey = useSetAtom(openLinkPopoverKeyAtom);

  const setOpen = React.useCallback(
    (next: boolean) => {
      // Opening claims the shared id (closing whoever held it); closing
      // only releases it if we're the current holder, so a stale close
      // from an unmounting sibling can't shut the active popover.
      setOpenKey((current) => (next ? id : current === id ? null : current));
    },
    [id, setOpenKey]
  );

  // Release the shared id if this popover unmounts while open (e.g. its
  // row is virtualised out of the list) so it can't silently reopen when
  // the row remounts under a fresh id.
  React.useEffect(
    () => () => setOpenKey((current) => (current === id ? null : current)),
    [id, setOpenKey]
  );

  return [open, setOpen] as const;
}
